/**
 * useDocumentStorage Hook
 * Unified document storage with cloud (DynamoDB/S3 via API) for authenticated users
 * and localStorage fallback for guests.
 *
 * Replaces the old documentStorage.ts facade — all cloud/local switching is managed
 * by React state derived from auth context instead of module-level mutable state.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { documentsApi } from '@/services/documentsApi';
import { API_URL } from '@/lib/apiConfig';
import { createLogger } from '@/lib/logger';
import type { SavedDocument, DocumentCategory } from '@/lib/types';

const log = createLogger('useDocumentStorage');

// Re-export types for consumers that were importing from documentStorage
export type { SavedDocument, DocumentCategory } from '@/lib/types';

// Sync types (only used in document storage context)
export interface SyncResult {
  success: boolean;
  syncedCount: number;
  failedCount: number;
  errors: Array<{ docId: string; error: string }>;
}

// ============================================
// localStorage Helpers (module-private)
// ============================================

function getStorageKey(userId: string | null): string {
  return userId ? `systemdox_docs_user_${userId}` : 'systemdox_docs_guest';
}

function getSyncMetadataKey(userId: string | null): string {
  return userId ? `systemdox_sync_user_${userId}` : 'systemdox_sync_guest';
}

function generateId(): string {
  return `doc_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

function extractTitle(content: string): string {
  const lines = content.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('# ')) return trimmed.slice(2).trim();
  }
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length > 0) return trimmed.slice(0, 50) + (trimmed.length > 50 ? '...' : '');
  }
  return 'Untitled Document';
}

function getLocalDocuments(userId: string | null): SavedDocument[] {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (!stored) return [];
    return JSON.parse(stored) as SavedDocument[];
  } catch (error) {
    log.error('Failed to load from localStorage', error as Error);
    return [];
  }
}

function saveLocalDocuments(userId: string | null, documents: SavedDocument[]): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(documents));
}

// ============================================
// Hook Return Type
// ============================================

export interface UseDocumentStorageReturn {
  isReady: boolean;
  isCloudEnabled: boolean;
  error: string | null;
  localDocumentCount: number;
  pendingSyncCount: number;
  isSyncing: boolean;
  syncError: string | null;
  lastSyncAt: string | null;
  syncToCloud: () => Promise<SyncResult>;
  getAllDocuments: () => Promise<SavedDocument[]>;
  getDocumentsByCategory: (category: DocumentCategory) => Promise<SavedDocument[]>;
  getDocumentsByProject: (projectId: string) => Promise<SavedDocument[]>;
  getDocumentsByProjectAndCategory: (projectId: string, category: DocumentCategory) => Promise<SavedDocument[]>;
  getDocumentById: (id: string) => Promise<SavedDocument | null>;
  saveDocument: (
    content: string,
    category: DocumentCategory,
    templateType: string,
    title?: string,
    projectId?: string,
    projectName?: string
  ) => Promise<SavedDocument>;
  updateDocument: (
    id: string,
    updates: Partial<Pick<SavedDocument, 'title' | 'content' | 'projectId' | 'projectName'>>
  ) => Promise<SavedDocument | null>;
  deleteDocument: (id: string) => Promise<boolean>;
  getDocumentCounts: () => Promise<Record<DocumentCategory, number>>;
}

// ============================================
// Hook
// ============================================

export function useDocumentStorage(): UseDocumentStorageReturn {
  const { isAuthenticated, isLoading: authLoading, user } = useAuthContext();
  const userId = user?.userId || null;
  const cloudEnabled = isAuthenticated && !!API_URL;

  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localDocumentCount, setLocalDocumentCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  // Initialize storage
  useEffect(() => {
    if (authLoading) return;

    setLocalDocumentCount(getLocalDocuments(userId).length);

    // Load sync metadata
    try {
      const stored = localStorage.getItem(getSyncMetadataKey(userId));
      if (stored) {
        const meta = JSON.parse(stored);
        setLastSyncAt(meta.lastSyncAt);
      }
    } catch { /* ignore */ }

    if (cloudEnabled) {
      log.info('Document storage initialized for authenticated user');
    } else {
      log.debug(isAuthenticated ? 'API URL not configured' : 'Using localStorage (unauthenticated user)');
    }

    setIsReady(true);
  }, [isAuthenticated, authLoading, userId, cloudEnabled]);

  // Document operations — each tries cloud first, falls back to localStorage
  const handleGetAllDocuments = useCallback(async () => {
    if (cloudEnabled) {
      try { return await documentsApi.getAllDocuments(); }
      catch (err) { log.error('Cloud fetch failed, falling back to localStorage', err as Error); }
    }
    return getLocalDocuments(userId);
  }, [cloudEnabled, userId]);

  const handleGetByCategory = useCallback(async (category: DocumentCategory) => {
    if (cloudEnabled) {
      try { return await documentsApi.getDocumentsByCategory(category); }
      catch (err) { log.error('Cloud fetch by category failed', err as Error); }
    }
    return getLocalDocuments(userId).filter(doc => doc.category === category);
  }, [cloudEnabled, userId]);

  const handleGetByProject = useCallback(async (projectId: string) => {
    if (cloudEnabled) {
      try { return await documentsApi.getDocumentsByProject(projectId); }
      catch (err) { log.error('Cloud fetch by project failed', err as Error); }
    }
    return getLocalDocuments(userId).filter(doc => doc.projectId === projectId);
  }, [cloudEnabled, userId]);

  const handleGetByProjectAndCategory = useCallback(
    async (projectId: string, category: DocumentCategory) => {
      if (cloudEnabled) {
        try { return await documentsApi.getDocumentsByProjectAndCategory(projectId, category); }
        catch (err) { log.error('Cloud fetch by project/category failed', err as Error); }
      }
      return getLocalDocuments(userId).filter(doc => doc.projectId === projectId && doc.category === category);
    },
    [cloudEnabled, userId]
  );

  const handleGetById = useCallback(async (id: string) => {
    if (cloudEnabled) {
      try { return await documentsApi.getDocumentById(id); }
      catch (err) { log.error('Cloud fetch by ID failed', err as Error); }
    }
    return getLocalDocuments(userId).find(doc => doc.id === id) || null;
  }, [cloudEnabled, userId]);

  const handleSave = useCallback(async (
    content: string,
    category: DocumentCategory,
    templateType: string,
    customTitle?: string,
    projectId?: string,
    projectName?: string
  ) => {
    if (cloudEnabled) {
      try {
        return await documentsApi.saveDocument(content, category, templateType, customTitle, projectId, projectName);
      } catch (err) {
        log.error('Cloud save failed, falling back to localStorage', err as Error);
      }
    }
    const now = new Date().toISOString();
    const document: SavedDocument = {
      id: generateId(),
      title: customTitle || extractTitle(content),
      content,
      category,
      templateType,
      createdAt: now,
      updatedAt: now,
      projectId,
      projectName,
    };
    const documents = getLocalDocuments(userId);
    documents.unshift(document);
    saveLocalDocuments(userId, documents);
    return document;
  }, [cloudEnabled, userId]);

  const handleUpdate = useCallback(async (
    id: string,
    updates: Partial<Pick<SavedDocument, 'title' | 'content' | 'projectId' | 'projectName'>>
  ) => {
    if (cloudEnabled) {
      try { return await documentsApi.updateDocument(id, updates); }
      catch (err) { log.error('Cloud update failed, falling back to localStorage', err as Error); }
    }
    const documents = getLocalDocuments(userId);
    const index = documents.findIndex(doc => doc.id === id);
    if (index === -1) return null;
    const updated: SavedDocument = { ...documents[index], ...updates, updatedAt: new Date().toISOString() };
    if (updates.content && !updates.title) updated.title = extractTitle(updates.content);
    documents[index] = updated;
    saveLocalDocuments(userId, documents);
    return updated;
  }, [cloudEnabled, userId]);

  const handleDelete = useCallback(async (id: string) => {
    if (cloudEnabled) {
      try { return await documentsApi.deleteDocument(id); }
      catch (err) { log.error('Cloud delete failed, falling back to localStorage', err as Error); }
    }
    const documents = getLocalDocuments(userId);
    const filtered = documents.filter(doc => doc.id !== id);
    if (filtered.length === documents.length) return false;
    saveLocalDocuments(userId, filtered);
    return true;
  }, [cloudEnabled, userId]);

  const handleGetCounts = useCallback(async () => {
    if (cloudEnabled) {
      try { return await documentsApi.getDocumentCounts(); }
      catch (err) { log.error('Cloud count failed, falling back to localStorage', err as Error); }
    }
    const documents = getLocalDocuments(userId);
    return {
      notes: documents.filter(d => d.category === 'notes').length,
      requirements: documents.filter(d => d.category === 'requirements').length,
      architecture: documents.filter(d => d.category === 'architecture').length,
    };
  }, [cloudEnabled, userId]);

  const handleSyncToCloud = useCallback(async (): Promise<SyncResult> => {
    if (!userId) {
      return { success: false, syncedCount: 0, failedCount: 0, errors: [{ docId: '', error: 'Must be logged in to sync' }] };
    }
    setIsSyncing(true);
    setSyncError(null);

    try {
      const localDocs = getLocalDocuments(userId);
      if (localDocs.length === 0) {
        return { success: true, syncedCount: 0, failedCount: 0, errors: [] };
      }

      // Bulk sync via batch API — chunks of 50
      let totalSynced = 0;
      const allErrors: Array<{ docId: string; error: string }> = [];

      for (let i = 0; i < localDocs.length; i += 50) {
        const batch = localDocs.slice(i, i + 50);
        try {
          const result = await documentsApi.syncDocuments(batch);
          totalSynced += result.syncedCount;
          allErrors.push(...result.errors);
        } catch (err) {
          // If the batch API fails, record errors for each doc in this batch
          const msg = err instanceof Error ? err.message : 'Batch sync failed';
          batch.forEach(doc => allErrors.push({ docId: doc.id, error: msg }));
        }
      }

      localStorage.setItem(getSyncMetadataKey(userId), JSON.stringify({
        lastSyncAt: new Date().toISOString(),
        syncedCount: totalSynced,
        failedCount: allErrors.length,
      }));

      if (allErrors.length === 0) {
        setLastSyncAt(new Date().toISOString());
      } else {
        setSyncError(allErrors[0].error);
      }

      setLocalDocumentCount(getLocalDocuments(userId).length);
      return { success: allErrors.length === 0, syncedCount: totalSynced, failedCount: allErrors.length, errors: allErrors };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      setSyncError(message);
      return { success: false, syncedCount: 0, failedCount: 0, errors: [{ docId: '', error: message }] };
    } finally {
      setIsSyncing(false);
    }
  }, [userId]);

  return {
    isReady,
    isCloudEnabled: cloudEnabled,
    error,
    localDocumentCount,
    pendingSyncCount: cloudEnabled ? 0 : localDocumentCount,
    isSyncing,
    syncError,
    lastSyncAt,
    syncToCloud: handleSyncToCloud,
    getAllDocuments: handleGetAllDocuments,
    getDocumentsByCategory: handleGetByCategory,
    getDocumentsByProject: handleGetByProject,
    getDocumentsByProjectAndCategory: handleGetByProjectAndCategory,
    getDocumentById: handleGetById,
    saveDocument: handleSave,
    updateDocument: handleUpdate,
    deleteDocument: handleDelete,
    getDocumentCounts: handleGetCounts,
  };
}

export default useDocumentStorage;
